<script setup lang="ts">
import { ref } from 'vue';
import HeadingSecondary from '@/components/general/HeadingSecondary.vue';

interface Invitee {
  name: string;
  email: string;
}

interface Emits {
  (e: 'submit', form: Invitee[]): void;
}

const emit = defineEmits<Emits>();

const form = ref<Invitee[]>([
  {
    name: '',
    email: '',
  },
]);

const formFields = ref({
  name: {
    type: 'text',
    icon: 'user',
    placeholder: 'Name',
  },
  email: {
    type: 'email',
    icon: 'mail',
    placeholder: 'Email',
  },
});

const errors = ref<Record<number, Record<string, boolean>>>({});
const touched = ref<Record<number, Record<string, boolean>>>({});

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateField = (index: number, field: string) => {
  if (!touched.value[index]) {
    touched.value[index] = {};
  }
  touched.value[index][field] = true;

  if (!errors.value[index]) {
    errors.value[index] = {};
  }

  const value = form.value[index][field as keyof Invitee];

  if (!value || value.trim() === '') {
    errors.value[index][field] = true;
  } else if (field === 'email' && !isValidEmail(value)) {
    errors.value[index][field] = true;
  } else {
    errors.value[index][field] = false;
  }
};

const hasFieldError = (index: number, field: string): boolean => {
  return touched.value[index]?.[field] && errors.value[index]?.[field];
};

const validateForm = (): boolean => {
  let isValid = true;

  form.value.forEach((invitee, index) => {
    Object.keys(formFields.value).forEach(field => {
      validateField(index, field);
      if (errors.value[index]?.[field]) {
        isValid = false;
      }
    });
  });

  return isValid;
};

const submitForm = () => {
  if (!validateForm()) {
    return;
  }
  emit('submit', form.value);
};

const reset = () => {
  form.value = [
    {
      name: '',
      email: '',
    },
  ];
  errors.value = {};
  touched.value = {};
};

const addInvitee = () => {
  const newIndex = form.value.length;
  form.value.push({
    name: '',
    email: '',
  });

  errors.value[newIndex] = {};
  touched.value[newIndex] = {};
};

const removeInvitee = () => {
  if (form.value.length > 1) {
    const removedIndex = form.value.length - 1;
    form.value.pop();

    delete errors.value[removedIndex];
    delete touched.value[removedIndex];
  }
};

defineExpose({
  reset,
});
</script>

<template>
  <div>
    <HeadingSecondary title="Invite Organization Member" />
    <form>
      <div v-for="(invitee, index) in form" :key="`invitee-${index}`" class="uk-margin-small">
        <div class="uk-flex uk-flex-row">
          <form-group
            v-for="f in Object.keys(formFields)"
            :key="`invitee-${index}-${f}`"
            class="uk-margin-small-right"
            :class="{ error: hasFieldError(index, f) }"
          >
            <span class="uk-inline">
              <span
                v-if="formFields[f].icon"
                class="uk-form-icon"
                :uk-icon="`icon: ${formFields[f].icon}`"
              ></span>
              <input
                class="uk-input"
                :type="formFields[f].type"
                :placeholder="formFields[f].placeholder"
                v-model="invitee[f]"
                required
                @blur="validateField(index, f)"
              />
            </span>
            <div class="error" v-if="hasFieldError(index, f)">
              {{ f[0].toUpperCase() + f.substring(1) }} is required{{
                f === 'email' && invitee[f] && !isValidEmail(invitee[f])
                  ? ' and must be a valid email'
                  : ''
              }}.
            </div>
          </form-group>
        </div>
      </div>

      <div class="uk-flex uk-flex-row uk-margin">
        <div class="uk-button uk-button-default uk-border-rounded" @click="addInvitee">
          Add Invitee
        </div>
        <div
          class="uk-button uk-button-default uk-margin-small-left uk-border-rounded"
          @click="removeInvitee"
        >
          Remove Invitee
        </div>
      </div>

      <div class="uk-margin">
        <button
          class="uk-button uk-button-default uk-button-primary uk-border-rounded"
          @click.prevent="submitForm"
          @keypress.enter="submitForm"
        >
          Invite
        </button>
      </div>
    </form>
  </div>
</template>
